
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// module components
import RppGeneralTabs from '@/modules/RPPModule/components/RppGeneralTabs/index.vue'
import RPPListItem from '@/modules/RPPModule/components/RPPListItem/index.vue'
import RPPListFilters from '@/modules/RPPModule/components/RPPListFilters/index.vue'
import ExportExcelModal from '@/modules/RPPModule/components/ExportExcelModal/index.vue'
import RppMassChangeStatus from '@/modules/RPPModule/components/RppMassChangeStatus/index.vue'

// types
import { RPPListParamsT, RPPT } from '@/core/types/rpp.types'

// realisations
import { rpp } from '@/core/realisations/rpp.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        // 'the-button': TheButton,
        // 'the-input': TheInput,
        // 'the-select': TheSelect,
        'failed-fetch': FailedFetch,
        List,
        'rpp-general-tabs': RppGeneralTabs,
        'rpp-list-item': RPPListItem,
        'rpp-list-filters': RPPListFilters,
        'export-excel-modal': ExportExcelModal,
        NewPagination,
        NewLimit,
        PageTitle,
        RppMassChangeStatus,
        NewButton
    },
    setup() {
        const state = reactive({
            is_loading: false,
            is_export: false,
            paymentBySupplierIds: [] as number[]
        })

        const mass_confirm_state = reactive({
            is_opened: false,
        })

        const mass_decline_state = reactive({
            is_opened: false,
        })

        const data_state = reactive({
            rpp: new DataState<RPPT>({}),
        })

        const filter_state = reactive({
            id: undefined as number | undefined,
            ftl_order_id: undefined as number | undefined,
            turnover_date_from: undefined as string | undefined,
            turnover_date_to: undefined as string | undefined,
            company_id: undefined as number | undefined,
            act_number: undefined as string | undefined,
            act_date: undefined as string | undefined,
            status_id: undefined as number | undefined,
            from_city_id: undefined as number | undefined,
            where_city_id: undefined as number | undefined,
            user_id: undefined as number | undefined,
            limit: 50,
        })

        const loadRPPList = async (page: number) => {
            unselectAllPaymentBySupplierIds()
            data_state.rpp.pagination.current_page = page

            // filters
            const params = {
                page,
                ...filter_state,
            }

            try {
                state.is_loading = true
                const response = await rpp.getRPPList(params)

                data_state.rpp.list = response.data
                data_state.rpp.pagination.total_pages = response.meta.last_page
                data_state.rpp.pagination.total_items = response.meta.total

                data_state.rpp.is_failed = false
                data_state.rpp.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.rpp.failure.catchError(error)

                if (!data_state.rpp.failure.error_state.is_cancel) {
                    data_state.rpp.is_failed = true
                }
            } finally {
                if (!data_state.rpp.failure.error_state.is_cancel) {
                    data_state.rpp.is_loading = false
                }

                state.is_loading = false
            }
        }

        const onSearch = (rpp: RPPListParamsT) => {
            filter_state.id = rpp.id
            filter_state.ftl_order_id = rpp.ftl_order_id
            filter_state.turnover_date_from = rpp.turnover_date_from
            filter_state.turnover_date_to = rpp.turnover_date_to
            filter_state.company_id = rpp.company_id
            filter_state.act_number = rpp.act_number
            filter_state.act_date = rpp.act_date
            filter_state.status_id = rpp.status_id
            filter_state.from_city_id = rpp.from_city_id
            filter_state.where_city_id = rpp.where_city_id
            filter_state.user_id = rpp.user_id

            loadRPPList(1)
        }

        const onReset = () => {
            filter_state.id = undefined
            filter_state.ftl_order_id = undefined
            filter_state.turnover_date_from = undefined
            filter_state.turnover_date_to = undefined
            filter_state.company_id = undefined
            filter_state.act_number = undefined
            filter_state.act_date = undefined
            filter_state.status_id = undefined
            filter_state.from_city_id = undefined
            filter_state.where_city_id = undefined
            filter_state.user_id = undefined

            loadRPPList(1)
        }

        const onLimitChange = (limit: number) => {
            filter_state.limit = limit
            loadRPPList(data_state.rpp.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            loadRPPList(page)
        }

        const onChangeCompany = () => loadRPPList(1)

        const onRppDeleted = () => {
            loadRPPList(data_state.rpp.pagination.current_page)
        }

        onBeforeMount(() => {
            loadRPPList(1)
        })

        const onMassConfirm = () => {
            mass_confirm_state.is_opened = true
        }

        const onMassConfirmClose = () => {
            mass_confirm_state.is_opened = false
        }

        const onMassConfirmSuccess = () => {
            onMassConfirmClose()
            unselectAllPaymentBySupplierIds()
            loadRPPList(data_state.rpp.pagination.current_page)
        }

        const onMassDecline = () => {
            mass_decline_state.is_opened = true
        }

        const onMassDeclineClose = () => {
            mass_decline_state.is_opened = false
        }

        const onMassDeclineSuccess = () => {
            onMassDeclineClose()
            unselectAllPaymentBySupplierIds()
            loadRPPList(data_state.rpp.pagination.current_page)
        }

        const selectAllPaymentBySupplierIds = () => {
            state.paymentBySupplierIds = data_state.rpp.list.filter((paymentBySupplier) => paymentBySupplier.status_id === 11).map((paymentBySupplier) => paymentBySupplier.id)
        }

        const unselectAllPaymentBySupplierIds = () => {
            state.paymentBySupplierIds = []
        }

        const selectPaymentBySupplierId = (paymentBySupplier: RPPT) => {
            state.paymentBySupplierIds.push(paymentBySupplier.id)
        }

        const unselectPaymentBySupplierId = (paymentBySupplier: RPPT) => {
            const index = state.paymentBySupplierIds.findIndex(
                (id) => id === paymentBySupplier.id
            )

            if (index >= 0) {
                state.paymentBySupplierIds.splice(index, 1)
            }
        }

        const onConfirmItem = (item: RPPT) => {
            state.paymentBySupplierIds = [item.id]
            onMassConfirm()
        }

        return {
            state,
            mass_confirm_state,
            mass_decline_state,
            data_state,
            filter_state,
            // RPP
            loadRPPList,
            onSearch,
            onReset,
            onLimitChange,
            onChangePage,
            onChangeCompany,
            onRppDeleted,
            onMassConfirm,
            onMassConfirmClose,
            onMassConfirmSuccess,
            onMassDecline,
            onMassDeclineClose,
            onMassDeclineSuccess,
            selectAllPaymentBySupplierIds,
            selectPaymentBySupplierId,
            unselectPaymentBySupplierId,
            onConfirmItem
        }
    },
})
